import React from 'react';

/**
 * StageTrack — one tracker row: a piece of work on one line against the same
 * four stops (Epic 5 · S5.1, Part F).
 *
 * Layout (≥ md):  [ title (wraps 2 lines) ][ four stops at fixed positions ][ when chip ]
 * Layout (< md):  title + when on top, then four dots in a line with only the
 *                 current stop's label — no horizontal scroll.
 *
 * Stops come from `item.steps[]` (4 entries, `{ key, label, state }`):
 *   - 'done'    → filled (reached)
 *   - 'current' → filled, larger, with a ring (the item is here now)
 *   - 'todo'    → hollow (not reached)
 *
 * Colour (Part C — colour is NEVER the only carrier; every stop keeps its
 * label in the group header): reached stops + segments between two reached
 * stops → brand #32B1FF; the reached Done (last) stop → emerald; the current
 * stop → the act-on-this accent ONLY when holder === 'client' (the single
 * urgency on the row); segments ahead of the current stop → faint. No
 * red/amber anywhere.
 *
 * `at_label` under each stop in `.gi-label` muted; null → no text, never a
 * placeholder. Whole row ≥44px, one tap → ItemDetail. Hover shows holder_label
 * (title attribute); on phone holder_label is written under the title.
 */

const BRAND = '#32B1FF';
const EMERALD = '#10B981';
// 7-col grid shared by dots, segments, the group stop-label header and the
// at_label row — identical template ⇒ four stops land at the same position in
// every row of a group, so rows line up into columns.
const STOPS_GRID = 'grid grid-cols-[auto_1fr_auto_1fr_auto_1fr_auto] items-center';

const reached = (step) => step?.state === 'done' || step?.state === 'current';

function dotClasses(step, idx, steps, holder) {
  const isCurrent = step?.state === 'current';
  const isDone = step?.state === 'done';
  const isLast = idx === steps.length - 1;
  const size = isCurrent ? 'w-4 h-4' : 'w-2.5 h-2.5';
  const ring = isCurrent ? `ring-2 ring-offset-2 ring-[${BRAND}] dark:ring-offset-gray-900` : '';
  let fill;
  if (!reached(step)) {
    fill = 'bg-transparent border-2 border-gray-300 dark:border-gray-600';
  } else if (isDone && isLast) {
    fill = `border-2 border-[${EMERALD}] bg-[${EMERALD}]`;
  } else {
    fill = `border-2 border-[${BRAND}] bg-[${BRAND}]`;
  }
  return `${size} ${fill} ${ring} rounded-full shrink-0`.trim();
}

// Solid brand only when BOTH ends are reached; the run ahead of the current
// stop (towards a not-reached stop) is faint.
function segmentClasses(a, b) {
  if (reached(a) && reached(b)) return `bg-[${BRAND}]`;
  return 'bg-gray-200 dark:bg-gray-700';
}

const PhoneDots = ({ steps, holder }) => {
  const current = steps.find((s) => s.state === 'current');
  return (
    <div className="flex md:hidden items-center gap-1.5 mt-2">
      {steps.map((s, i) => (
        <React.Fragment key={s.key ?? i}>
          {i > 0 && <span className={`h-0.5 flex-1 rounded ${segmentClasses(steps[i - 1], s)}`} />}
          <span
            className={dotClasses(s, i, steps, holder).replace('ring-offset-2', '').replace(/ring-\[#32B1FF\]/, '')}
            data-state={s.state}
          />
        </React.Fragment>
      ))}
      {current && (
        <span className="gi-label text-gray-500 dark:text-gray-400 ml-2 normal-case tracking-normal">
          {current.label}
        </span>
      )}
    </div>
  );
};

const StageTrack = ({ item, onOpenItem }) => {
  const steps = Array.isArray(item?.steps) ? item.steps : [];
  const holder = item?.holder;
  const holderLabel = item?.holder_label;
  const urgent = steps.some((s) => s.state === 'current') && holder === 'client';

  return (
    <li>
      <button
        type="button"
        onClick={() => onOpenItem?.(item)}
        title={holderLabel ? `With ${holderLabel}` : undefined}
        data-testid="stage-track"
        data-item-id={item?.id}
        data-urgent={urgent ? 'true' : undefined}
        className="w-full min-h-[44px] flex items-center gap-3 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-800/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#32B1FF] rounded-lg transition-colors"
      >
        {/* Title + phone-only holder label. Wraps 2 lines. */}
        <div className="flex-1 min-w-0">
          <div className="gi-body font-medium text-gray-900 dark:text-gray-100 line-clamp-2 break-words">
            {item?.title}
          </div>
          {holderLabel && (
            <div className="md:hidden text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">
              With {holderLabel}
            </div>
          )}
        </div>

        {/* Desktop/tablet stops: the fixed-position four-stop grid + at_labels. */}
        <div className="hidden md:block w-56 shrink-0">
          <div className={STOPS_GRID}>
            {steps.map((s, i) => (
              <React.Fragment key={s.key ?? i}>
                {i > 0 && (
                  <span className={`h-0.5 w-full rounded ${segmentClasses(steps[i - 1], s)}`} />
                )}
                <span className="flex justify-center">
                  <span className={dotClasses(s, i, steps, holder)} data-state={s.state} />
                </span>
              </React.Fragment>
            ))}
          </div>
          {/* at_labels — aligned second grid; null → no text, never a placeholder. */}
          <div className={`${STOPS_GRID} mt-1`}>
            {steps.map((s, i) => (
              <React.Fragment key={s.key ?? i}>
                {i > 0 && <span />}
                <span className="gi-label text-gray-400 dark:text-gray-500 text-center normal-case tracking-normal truncate">
                  {s.at_label || ''}
                </span>
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* When chip — null → nothing. */}
        <div className="shrink-0 w-20 text-right">
          {item?.when && (
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300">
              {item.when}
            </span>
          )}
        </div>
      </button>

      {/* Phone stops — dots in one line, current label only. */}
      <PhoneDots steps={steps} holder={holder} />
    </li>
  );
};

export default StageTrack;

